import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { X, ScanBarcode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

interface BarcodeScannerProps {
  open: boolean;
  onClose: () => void;
}

export const BarcodeScanner = ({ open, onClose }: BarcodeScannerProps) => {
  const [scanning, setScanning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const navigate = useNavigate();
  const { language } = useLanguage();

  const t = {
    title: language === 'en' ? 'Scan Product Barcode' : 'Skenuoti produkto kodą',
    instruction: language === 'en' ? 'Position the barcode within the frame' : 'Išdėstykite brūkšninį kodą rėmelyje',
    close: language === 'en' ? 'Close' : 'Uždaryti',
    searching: language === 'en' ? 'Searching for product...' : 'Ieškoma produkto...',
    notFound: language === 'en' ? 'Product not found in our catalog' : 'Produktas nerastas mūsų kataloge',
    error: language === 'en' ? 'Failed to start scanner. Please check camera permissions.' : 'Nepavyko paleisti skaitytuvo. Patikrinkite kameros leidimus.',
  };

  useEffect(() => {
    if (open && !scanning) {
      startScanner();
    }
    return () => {
      stopScanner();
    };
  }, [open]);

  // Handle browser back button to close scanner
  useEffect(() => {
    const handlePopState = async (event: PopStateEvent) => {
      if (open) {
        event.preventDefault();
        await stopScanner();
        onClose();
        // Push the state back to stay on the same page
        window.history.pushState(null, '', window.location.href);
      }
    };

    if (open) {
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [open, onClose]);

  const startScanner = async () => {
    try {
      // Request camera permission explicitly
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" } 
      });
      
      // Stop the test stream immediately
      stream.getTracks().forEach(track => track.stop());
      
      // Now start the scanner
      const scanner = new Html5Qrcode("barcode-reader");
      scannerRef.current = scanner;
      
      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        onScanSuccess,
        onScanError
      );
      
      setScanning(true);
    } catch (err) {
      console.error("Error starting scanner:", err);
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        toast.error(language === 'en' ? 'Camera permission denied. Please allow camera access in your browser settings.' : 'Kameros leidimas atmestas. Leiskite kameros prieigą naršyklės nustatymuose.');
      } else {
        toast.error(t.error);
      }
      onClose();
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
        setScanning(false);
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
  };

  const onScanSuccess = async (decodedText: string) => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    console.log("Scanned barcode:", decodedText);
    await stopScanner();
    
    const loadingToast = toast.loading(t.searching);
    
    try {
      // Search for product by product_code
      const { data: products, error } = await supabase
        .from('products')
        .select('*')
        .eq('product_code', decodedText)
        .eq('is_inactive', false)
        .limit(1);
      
      toast.dismiss(loadingToast);
      
      if (error || !products || products.length === 0) {
        toast.error(t.notFound);
        onClose();
        return;
      }
      
      const product = products[0];
      navigate(`/product/${product.slug || product.id}`);
      onClose();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(t.notFound);
      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  const onScanError = (error: string) => {
    // Ignore scan errors (they happen continuously while scanning)
    if (!error.includes("NotFoundException")) {
      console.debug("Scan error:", error);
    }
  };

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanBarcode className="h-5 w-5" />
            {t.title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            {t.instruction}
          </p>
          
          <div 
            id="barcode-reader" 
            className="w-full rounded-lg overflow-hidden border border-border"
            style={{ minHeight: '300px' }}
          />
          
          <Button 
            onClick={handleClose} 
            variant="outline" 
            className="w-full"
          >
            <X className="h-4 w-4 mr-2" />
            {t.close}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
